import { Container, Graphics, Rectangle } from "pixi.js"
import { TextRenderer } from "../text/text_renderer"
import { Fader } from "./fader"

// The recruit role picker: a small "choose a role" panel that floats above a freshly-arrived ember
// recruit. Two framed choice cards (Forager / Defender) sit under a title, with a downward pointer
// aimed at the recruit. Picking a card assigns that role (the server swaps the recruit into the
// matching script). Styled to match the command wheel's ember-lit look (warm amber rim, dark glass)
// and drawn entirely here — the public API (bind/fade) mirrors CommandWheel so GuiManager can drive
// it the same way.

export type RecruitRole = "forager" | "defender"

// Fade timing — mirrors CommandWheel (~100ms each way).
const FADE_INTERVAL_MS = 5
const FADE_STEP = 0.05

// Ember-lit palette + geometry.
const RIM = 0xf2a94e // warm amber rim / glow
const PANEL_FILL = 0x1b1512
const PANEL_ALPHA = 0.92
const CARD_IDLE_FILL = 0x2a201a
const CARD_HOVER_FILL = 0x33271e
const CARD_IDLE_RIM = 0x6b4f38
const TEXT_COLOR = 0xfff3e0

const PAD = 12
const TITLE_H = 20
const CARD_W = 96
const CARD_H = 104
const CARD_GAP = 12
const TAIL_H = 12
const TAIL_HALF_W = 9
const PANEL_CORNER = 10
const CARD_CORNER = 8

// Overall footprint scale. The stage applies a 1.3× vertical scale, so keep the widget compact
// enough that it still floats clear of the recruit near the top of the screen.
const WIDGET_SCALE = 0.82

const PANEL_W = 2 * CARD_W + CARD_GAP + 2 * PAD
const PANEL_H = TITLE_H + CARD_H + 2 * PAD
// Origin (0,0) is the tail tip (points down at the recruit); the panel body floats above it.
const PANEL_LEFT = -PANEL_W / 2
const PANEL_TOP = -(TAIL_H + PANEL_H)
const CARD_Y = PANEL_TOP + PAD + TITLE_H
const CARD_X0 = PANEL_LEFT + PAD
const CARD_X1 = CARD_X0 + CARD_W + CARD_GAP

interface RoleMeta {
    role: RecruitRole
    emoji: string
    title: string
    // Two short description lines (pre-split to avoid width guesswork).
    desc: [string, string]
    // Role accent, matched to the ember roster's role colors so the whole ember UI reads as one set.
    color: number
}

const ROLE_CARDS: RoleMeta[] = [
    { role: "forager", emoji: "🪓", title: "Forager", desc: ["Gathers wood", "to feed the fire"], color: 0x7be08a },
    { role: "defender", emoji: "🛡️", title: "Defender", desc: ["Guards the camp", "and the Lord"], color: 0xff8a5c },
]

interface CardHandle {
    container: Container
    bg: Graphics
    meta: RoleMeta
    hovered: boolean
}

export interface RolePickerProps {
    onSelect?: (role: RecruitRole) => void
}

export class RolePicker extends Container {
    private onSelect?: (role: RecruitRole) => void
    private bg: Graphics
    private cards: CardHandle[] = []

    // The recruit this picker is bound to (so GuiManager can keep it glued over that NPC and scope
    // the resulting command to it). Undefined while closed.
    private targetEntityId?: string

    // Whether the downward pointer is drawn. The tail is a claim -- "this panel is about whoever is
    // standing under its tip" -- so it comes off when there is no longer anybody there (see
    // setTailVisible).
    private tailVisible: boolean = true

    // Logical open flag — flips the instant a fade starts so input gating frees immediately (mirrors
    // CommandWheel.isOpen semantics). Only one fader runs at a time.
    private opened: boolean = false
    private fadeInFader: Fader | null = null
    private fadeOutFader: Fader | null = null

    constructor(props: RolePickerProps = {}) {
        super()
        this.onSelect = props.onSelect
        this.scale.set(WIDGET_SCALE)

        // Swallow pointer events over the panel so clicks on the widget never fall through to a world
        // attack (the cards handle their own clicks; this covers the gaps/title too).
        this.interactive = true
        this.hitArea = new Rectangle(PANEL_LEFT, PANEL_TOP, PANEL_W, PANEL_H + TAIL_H)
        this.on("pointerdown", (event: any) => event.stopPropagation())
        this.on("pointermove", (event: any) => event.stopPropagation())
        this.on("rightclick", (event: any) => event.stopPropagation())
        this.on("contextmenu", (event: any) => {
            event.preventDefault()
            event.stopPropagation()
        })

        this.bg = new Graphics()
        this.addChild(this.bg)
        this.drawBackground()
        this.addTitle()

        this.cards = [this.makeCard(ROLE_CARDS[0], CARD_X0), this.makeCard(ROLE_CARDS[1], CARD_X1)]

        this.visible = false
    }

    // Dark "glass" panel with a warm rim, a faint outer glow (a fatter low-alpha copy — cheaper than a
    // blur filter, which matters on low-end GPUs), and a downward pointer whose tip sits at the origin.
    // The tail is drawn as a filled triangle overlapping the body's bottom edge with only its two
    // diagonals stroked, so there's no seam where it meets the body. Nothing below the body is drawn
    // at all once the tail is off, so the panel ends at its own bottom edge.
    private drawBackground() {
        const g = this.bg
        g.clear()

        // Soft outer glow.
        g.beginFill(RIM, 0.06)
        g.drawRoundedRect(PANEL_LEFT - 5, PANEL_TOP - 5, PANEL_W + 10, PANEL_H + 10, PANEL_CORNER + 5)
        g.endFill()

        // Body.
        g.lineStyle(2, RIM, 0.9)
        g.beginFill(PANEL_FILL, PANEL_ALPHA)
        g.drawRoundedRect(PANEL_LEFT, PANEL_TOP, PANEL_W, PANEL_H, PANEL_CORNER)
        g.endFill()

        if (!this.tailVisible) {
            return
        }

        // Tail fill (base tucked 1px up into the body so the join is hidden).
        const baseY = -TAIL_H - 1
        g.lineStyle(0)
        g.beginFill(PANEL_FILL, PANEL_ALPHA)
        g.moveTo(-TAIL_HALF_W, baseY)
        g.lineTo(0, 0)
        g.lineTo(TAIL_HALF_W, baseY)
        g.closePath()
        g.endFill()

        // Tail rim: stroke only the two visible diagonals (not the hidden base).
        g.lineStyle(2, RIM, 0.9)
        g.moveTo(-TAIL_HALF_W, baseY)
        g.lineTo(0, 0)
        g.lineTo(TAIL_HALF_W, baseY)
    }

    private addTitle() {
        const title = new TextRenderer()
            .color(TEXT_COLOR)
            .fontSize(13)
            .bold()
            .align("center")
            .render("Assign this recruit")
        title.x = -title.width / 2
        title.y = PANEL_TOP + PAD - 2
        title.alpha = 0.95
        this.addChild(title)
    }

    private makeCard(meta: RoleMeta, x: number): CardHandle {
        const container = new Container()
        container.x = x
        container.y = CARD_Y
        container.interactive = true
        container.cursor = "pointer"
        container.hitArea = new Rectangle(0, 0, CARD_W, CARD_H)

        const bg = new Graphics()
        container.addChild(bg)

        // Emoji (no color/tint so it keeps its own colors).
        const emoji = new TextRenderer().fontSize(34).align("center").render(meta.emoji)
        emoji.x = CARD_W / 2 - emoji.width / 2
        emoji.y = 12
        container.addChild(emoji)

        // Title in the role accent color.
        const title = new TextRenderer().color(meta.color).fontSize(15).bold().align("center").render(meta.title)
        title.x = CARD_W / 2 - title.width / 2
        title.y = 56
        container.addChild(title)

        // Two-line description.
        meta.desc.forEach((line, i) => {
            const t = new TextRenderer().color(TEXT_COLOR).fontSize(10).align("center").render(line)
            t.x = CARD_W / 2 - t.width / 2
            t.y = 78 + i * 12
            t.alpha = 0.82
            container.addChild(t)
        })

        const handle: CardHandle = { container, bg, meta, hovered: false }
        this.drawCard(handle)

        container.on("pointerover", () => {
            handle.hovered = true
            this.drawCard(handle)
        })
        container.on("pointerout", () => {
            handle.hovered = false
            this.drawCard(handle)
        })
        container.on("pointerdown", (event: any) => {
            event.stopPropagation()
            this.onSelect?.(meta.role)
        })

        this.addChild(container)
        return handle
    }

    // Draw (or redraw on hover) a card: idle = warm charcoal fill + muted rim; hovered = a soft glow
    // in the role color, a brighter fill, and a full role-color rim.
    private drawCard(handle: CardHandle) {
        const g = handle.bg
        const color = handle.meta.color
        g.clear()
        if (handle.hovered) {
            g.beginFill(color, 0.16)
            g.drawRoundedRect(-3, -3, CARD_W + 6, CARD_H + 6, CARD_CORNER + 3)
            g.endFill()
            g.lineStyle(2, color, 1)
            g.beginFill(CARD_HOVER_FILL, 0.97)
        } else {
            g.lineStyle(1.5, CARD_IDLE_RIM, 0.7)
            g.beginFill(CARD_IDLE_FILL, 0.9)
        }
        g.drawRoundedRect(0, 0, CARD_W, CARD_H, CARD_CORNER)
        g.endFill()
    }

    private clearHover() {
        this.cards.forEach(card => {
            if (card.hovered) {
                card.hovered = false
                this.drawCard(card)
            }
        })
    }

    // Bind the picker to a single recruit so GuiManager can keep it positioned over that NPC and scope
    // the chosen role back to it.
    public bindToEntity(targetEntityId: string) {
        this.targetEntityId = targetEntityId
        this.clearHover()
    }

    public clearTarget() {
        this.targetEntityId = undefined
    }

    // Show or hide the downward pointer. A recruit can walk off while the picker is up, and a tail
    // left behind would aim at bare ground -- or worse, at whoever wandered into that spot. Without
    // it the panel is just a panel, which is the honest thing to show. The hit area follows so the
    // widget stops swallowing clicks on a strip of world it no longer covers.
    public setTailVisible(visible: boolean) {
        if (this.tailVisible === visible) {
            return
        }
        this.tailVisible = visible
        this.hitArea = new Rectangle(PANEL_LEFT, PANEL_TOP, PANEL_W, PANEL_H + (visible ? TAIL_H : 0))
        this.drawBackground()
    }

    public getTargetEntityId(): string | undefined {
        return this.targetEntityId
    }

    public isOpen(): boolean {
        return this.opened
    }

    // Reveal by fading in, interrupting an in-progress dismiss (mirrors CommandWheel.fadeIn).
    public fadeIn() {
        this.opened = true

        if (this.fadeOutFader) {
            this.fadeOutFader.stop(true)
            this.fadeOutFader = null
        }
        if (this.fadeInFader) {
            return
        }

        if (!this.visible) {
            this.alpha = 0
        }
        this.visible = true

        if (this.alpha >= 1) {
            this.alpha = 1
            return
        }

        this.fadeInFader = new Fader(this, {
            direction: "in",
            minAlpha: 1.0,
            fadeInterval: FADE_INTERVAL_MS,
            fadeAlphaDecrement: FADE_STEP,
            releaseCallback: () => {
                this.fadeInFader = null
                this.alpha = 1
            },
        })
        this.fadeInFader.update()
    }

    // Dismiss by fading out, hiding once transparent (mirrors CommandWheel.fadeOut).
    public fadeOut() {
        this.opened = false
        this.clearHover()

        if (this.fadeInFader) {
            this.fadeInFader.stop(true)
            this.fadeInFader = null
        }
        if (this.fadeOutFader || !this.visible) {
            return
        }

        this.fadeOutFader = new Fader(this, {
            direction: "out",
            minAlpha: 0.0,
            fadeInterval: FADE_INTERVAL_MS,
            fadeAlphaDecrement: FADE_STEP,
            releaseCallback: () => {
                this.fadeOutFader = null
                this.visible = false
                this.alpha = 1.0
            },
        })
        this.fadeOutFader.update()
    }
}
